import { Component, computed, effect, inject, input, OnDestroy, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { faPlus, faRefresh, faTrashCan } from '@fortawesome/pro-regular-svg-icons';

import { Subscription } from 'rxjs';

import { BlockType, BlockLoopSourceEnum } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { IBlockForm } from '../../../../models/blocks/block-form.interface';
import { QuestionTypeConfig } from '../../../../models/question-type-config.constants';
import { SurveyEditorService } from '../../../../services/survey-editor.service';
import { GroupBlockQuestionPickerComponent } from '../../group-block-question-picker/group-block-question-picker.component';

@Component({
  selector: 'empathy-block-questions',
  templateUrl: 'block-questions.component.html',
  styleUrls: ['./block-questions.component.scss']
})
export class BlockQuestionsComponent implements OnDestroy {
  activeBlock = input.required<FormGroup<IBlockForm>>();
  isSecondChild = input<boolean>(false);

  siblingWithoutQuestions$ = signal<boolean>(false);
  nextQuestion$ = signal<Question>(null);

  surveyEditorService = inject(SurveyEditorService);

  questionRows$ = computed(() => {
    const questionsById = new Map(this.questions$().map((question) => [question.id, question]));
    const activeBlockId = this.activeBlock().value.id;
    const block = this.surveyEditorService
      .surveyEditor$()
      .blocks.all$()
      .find((entry) => entry.id === activeBlockId);
    const groupedQuestionIds = block?.groupedQuestionIds ?? [];

    return groupedQuestionIds
      .map((qId, groupedIndex) => ({
        showStimulus: block?.stimulusQuestionIds?.includes(qId) ?? false,
        isLast: groupedIndex === groupedQuestionIds.length - 1,
        question: questionsById.get(qId)
      }))
      .filter((row) => !!row.question);
  });

  readonly isMediaLoop$ = computed(() => {
    const block = this.surveyEditorService
      .surveyEditor$()
      .blocks.all$()
      .find((entry) => entry.id === this.activeBlock().value.id);
    return block?.type === BlockType.Loop && block.sourceType === BlockLoopSourceEnum.Media;
  });
  readonly missingBinding$ = computed(() => this.isMediaLoop$() && this.questionRows$().length > 0 && !this.questionRows$().some((row) => row.showStimulus));

  faPlus = faPlus;
  faRefresh = faRefresh;
  faTrashCan = faTrashCan;

  questionTypeConfig = QuestionTypeConfig;

  private dialog = inject(MatDialog);
  private questions$ = toSignal(this.surveyEditorService.questions$, { initialValue: [] as Question[] });

  private subscription: Subscription;

  constructor() {
    effect(
      () => {
        if ((this.activeBlock().get('groupedQuestionIds') as FormArray).length > 0 || this.isSecondChild()) {
          this.findNextQuestion();
        }
      },
      { allowSignalWrites: true }
    );

    this.subscription = this.surveyEditorService.surveyEditor$().blocks.refreshed$.subscribe(() => {
      if ((this.activeBlock().get('groupedQuestionIds') as FormArray).length > 0 || this.isSecondChild()) {
        this.findNextQuestion();
      }
    });
  }

  private get groupedQuestionIds(): FormArray {
    return this.activeBlock().get('groupedQuestionIds') as FormArray;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  onShowStimulus(questionId: string, selected: boolean): void {
    this.surveyEditorService.surveyEditor$().blocks.setStimulusQuestion(this.activeBlock().value.id, questionId, selected);
  }

  onAddQuestion(): void {
    let startFrom = 0;
    if (this.activeBlock().get('type').value === BlockType.Loop) {
      const sourceQIndex = this.surveyEditorService.surveyEditor$().questions.findIndex((q) => q.id === this.activeBlock().value.sourceQId);

      startFrom = sourceQIndex + 1;
    }

    const dialogRef = this.dialog.open(GroupBlockQuestionPickerComponent, {
      data: this.surveyEditorService.surveyEditor$().blocks.alloweQuestionsOnBlock(startFrom)
    });

    dialogRef.afterClosed().subscribe((questionId: string) => {
      if (!questionId) {
        return;
      }

      this.surveyEditorService.surveyEditor$().blocks.addQuestionToBlock(this.activeBlock().value.id, questionId);
      this.surveyEditorService.refreshQuestionsList();

      this.findNextQuestion();
    });
  }

  onAddNextSequentialQuestion(): void {
    this.surveyEditorService.surveyEditor$().blocks.addQuestionToBlock(this.activeBlock().value.id, this.nextQuestion$().id);
    this.surveyEditorService.refreshQuestionsList();

    this.findNextQuestion();
  }

  onRemoveLastQuestion(): void {
    this.surveyEditorService.surveyEditor$().blocks.removeLastQuestionFromBlock(this.activeBlock().value.id);
    this.surveyEditorService.refreshQuestionsList();

    this.findNextQuestion();
  }

  private findNextQuestion(): void {
    this.siblingWithoutQuestions$.set(false);
    this.nextQuestion$.set(null);

    const lastQId =
      this.groupedQuestionIds.length === 0
        ? this.surveyEditorService.surveyEditor$().blocks.getSiblingLastQuestionId(this.activeBlock().get('id').value)
        : this.groupedQuestionIds.at(this.groupedQuestionIds.length - 1).value;

    if (!lastQId) {
      this.siblingWithoutQuestions$.set(this.isSecondChild());
      return;
    }

    this.nextQuestion$.set(this.calculateNextQuestion(lastQId));
  }

  private calculateNextQuestion(lastQId: string): Question {
    const lastQIndex = this.surveyEditorService.surveyEditor$().getQuestionIndex(lastQId);

    if (lastQIndex === this.surveyEditorService.surveyEditor$().questions.length - 1) {
      return null;
    }

    const nextQ = this.surveyEditorService.surveyEditor$().questions[lastQIndex + 1];

    if (!nextQ) {
      return null;
    }

    const isInsideABlock = this.surveyEditorService.surveyEditor$().blocks.isOnBlock(nextQ.id);
    if (isInsideABlock) {
      this.nextQuestion$.set(null);
      return;
    }

    return nextQ;
  }
}
